import { useId, useRef, useState } from 'react';
import { validatePngFile, blobToObjectUrl, revokeObjectUrl } from '../utils/png';
import './MainPanel.css';

type Props = {
  previewUrl: string | null;
  busy: boolean;
  error: string | null;
  onChange: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
};

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 20h4L18.5 9.5a2.12 2.12 0 0 0-3-3L5 17v3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13.5 6.5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 7V5h6v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M7 7l1 12h8l1-12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SignatureView({ previewUrl, busy, error, onChange, onDelete }: Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const clearLocal = () => {
    revokeObjectUrl(localPreview);
    setLocalPreview(null);
    setPendingFile(null);
  };

  const onPick = (list: FileList | null) => {
    setLocalError(null);
    setConfirmDelete(false);
    const f = list?.[0];
    if (!f) return;
    const err = validatePngFile(f);
    if (err) {
      clearLocal();
      setLocalError(err);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    revokeObjectUrl(localPreview);
    setPendingFile(f);
    setLocalPreview(blobToObjectUrl(f));
  };

  const displayUrl = localPreview || previewUrl;

  return (
    <div className="mp-section">
      <h2 className="mp-h2">Meine Unterschrift</h2>

      {displayUrl ? (
        <div className={`mp-sig-card${pendingFile ? ' mp-sig-card--pending' : ''}`}>
          <img src={displayUrl} alt="Meine Unterschrift" className="mp-preview" />

          {!pendingFile ? (
            <div className="mp-sig-actions">
              <label
                className="mp-icon-btn"
                htmlFor={inputId}
                title="Unterschrift ändern"
                aria-label="Unterschrift ändern"
              >
                <IconEdit />
              </label>
              <button
                type="button"
                className="mp-icon-btn mp-icon-btn--danger"
                title="Unterschrift löschen"
                aria-label="Unterschrift löschen"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                <IconTrash />
              </button>
            </div>
          ) : null}

          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept="image/png,.png"
            className="mp-file"
            disabled={busy}
            onChange={(e) => onPick(e.target.files)}
          />
        </div>
      ) : (
        <p className="mp-muted">Vorschau wird geladen…</p>
      )}

      {confirmDelete && !pendingFile ? (
        <div className="mp-confirm">
          <p>Unterschrift wirklich löschen?</p>
          <div className="mp-confirm-row">
            <button
              type="button"
              className="sp-btn sp-btn--danger"
              disabled={busy}
              onClick={() => void onDelete()}
            >
              Löschen
            </button>
            <button
              type="button"
              className="sp-btn sp-btn--secondary"
              disabled={busy}
              onClick={() => setConfirmDelete(false)}
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}

      {pendingFile ? (
        <div className="mp-actions">
          <p className="mp-muted">Neue Datei — noch nicht gespeichert.</p>
          <button
            type="button"
            className="sp-btn sp-btn--primary"
            disabled={busy}
            onClick={async () => {
              if (!pendingFile) return;
              await onChange(pendingFile);
              clearLocal();
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            {busy ? 'Speichern…' : 'Unterschrift speichern'}
          </button>
          <button
            type="button"
            className="sp-btn sp-btn--secondary"
            disabled={busy}
            onClick={() => {
              clearLocal();
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            Abbrechen
          </button>
        </div>
      ) : null}

      {(localError || error) && (
        <div className="sp-error" role="alert">
          <p>{localError || error}</p>
        </div>
      )}
    </div>
  );
}
